import { useEffect, useState } from 'react';
import { getValueFromUserAttributes } from '../../util/UtilFunctions';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { SectionHeader } from './SectionHeader';
import ProfileModals from '../../app/views/accountSettings/accountSettingsProfile/profileModals/ProfileModals';

interface ProfileSectionProps {
  currentUserInfo: Record<string, unknown>;
  updateCurrentUserInfo: (...args: unknown[]) => void;
  updateIsQuerying: (querying: boolean) => void;
  getWorkspacePeopleList: () => void;
}

export function ProfileSection({
  currentUserInfo,
  updateCurrentUserInfo,
  updateIsQuerying,
  getWorkspacePeopleList,
}: ProfileSectionProps) {
  const [modalOpen, setModalOpen] = useState<string | undefined>();
  const [userData, setUserData] = useState({ firstname: '', lastname: '', email: '' });

  useEffect(() => {
    const cognitoAttrs = (currentUserInfo.cognitoAttributes as Record<string, unknown>)
      ?.UserAttributes;
    if (!cognitoAttrs) return;
    setUserData({
      firstname: getValueFromUserAttributes(cognitoAttrs, 'given_name') || '',
      lastname: getValueFromUserAttributes(cognitoAttrs, 'family_name') || '',
      email: getValueFromUserAttributes(cognitoAttrs, 'email') || '',
    });
  }, [currentUserInfo]);

  const imageUrl = (currentUserInfo.image as Record<string, string>)?.S;

  return (
    <>
      <div className="my-4">
        <SectionHeader>General</SectionHeader>
        <div className="max-w-[700px]">
          <Row label="Profile picture">
            <Avatar
              src={imageUrl && imageUrl !== 'no-image' ? imageUrl : null}
              name={`${userData.firstname} ${userData.lastname}`}
              size="lg"
            />
            <Button variant="ghost" size="sm" onClick={() => setModalOpen('uploadProfile')}>
              Update
            </Button>
          </Row>
          <Row label="First name">
            <span className="text-muted-foreground">{userData.firstname}</span>
            <Button variant="ghost" size="sm" onClick={() => setModalOpen('updateFirstname')}>
              Edit
            </Button>
          </Row>
          <Row label="Surname">
            <span className="text-muted-foreground">{userData.lastname}</span>
            <Button variant="ghost" size="sm" onClick={() => setModalOpen('updateLastname')}>
              Edit
            </Button>
          </Row>
          <Row label="Email" noBorder>
            <span className="text-muted-foreground">{userData.email}</span>
            <Button variant="ghost" size="sm" onClick={() => setModalOpen('updateEmail')}>
              Edit
            </Button>
          </Row>
        </div>
      </div>

      {modalOpen && (
        <ProfileModals
          modalOpen={modalOpen}
          currentUserInfo={currentUserInfo}
          handleCloseModalClick={() => setModalOpen(undefined)}
          updateCurrentUserInfo={updateCurrentUserInfo}
          updateIsQuerying={updateIsQuerying}
          getWorkspacePeopleList={getWorkspacePeopleList}
        />
      )}
    </>
  );
}

function Row({
  label,
  children,
  noBorder,
}: {
  label: string;
  children: React.ReactNode;
  noBorder?: boolean;
}) {
  return (
    <div
      className={`ml-1 flex items-center gap-4 py-3 ${noBorder ? '' : 'border-b border-border'}`}
    >
      <h5 className="w-32 shrink-0 text-base font-normal">{label}</h5>
      <div className="ml-auto flex items-center gap-3">{children}</div>
    </div>
  );
}
